import { Constants } from "@eagerdog/constants";

/**
 * UKC Obedience/Rally Jump Height Maps
 * Format: [min_withers, max_withers, jump_height, broad_height]
 */

const STANDARD_JUMP_HEIGHT_MAP: [number, number, number, number][] = [
  [0, 7, 6, 8],
  [7, 8, 6, 8],
  [8, 9, 8, 16],
  [9, 10, 8, 18],
  [10, 11, 10, 20],
  [11, 12, 10, 22],
  [12, 13, 12, 24],
  [13, 14, 12, 26],
  [14, 15, 14, 28],
  [15, 16, 14, 30],
  [16, 17, 16, 32],
  [17, 18, 16, 34],
  [18, 19, 18, 36],
  [19, 20, 18, 38],
  [20, 21, 20, 40],
  [21, 22, 20, 42],
  [22, 23, 22, 44],
  [23, 24, 22, 46],
  [24, 25, 24, 48],
  [25, 26, 24, 50],
  [26, 27, 26, 52],
  [27, 999, 26, 54],
];

const MINIMUM_JUMP_HEIGHT_MAP: [number, number, number, number][] = [
  [0, 10, 4, 8],
  [10, 13.5, 8, 16],
  [13.5, 16, 10, 20],
  [16, 18.5, 12, 24],
  [18.5, 21.5, 14, 28],
  [21.5, 24, 16, 32],
  [24, 26, 18, 36],
  [26, 29.5, 20, 40],
  [29.5, 32, 22, 44],
  [32, 999, 24, 48],
];

/**
 * Calculate UKC Obedience/Rally jump heights based on dog's height at withers and jump height type
 * 
 * @param height_at_withers - Dog's height at withers in inches
 * @param jump_height_type - Either "Minimum" or "Standard" jump height type
 * @returns Object containing jump_height and broad_height, or null if invalid input
 */
export function calculateUkcJumpHeights(
  height_at_withers: number,
  jump_height_type: string
): {
  jump_height: number;
  broad_height: number;
} | null {
  //
  if (
    ![
      Constants.jump_height_type.minimum,
      Constants.jump_height_type.standard,
    ].includes(jump_height_type)
  ) {
    return null;
  }

  const jumpHeightMap =
    jump_height_type === Constants.jump_height_type.minimum
      ? MINIMUM_JUMP_HEIGHT_MAP
      : STANDARD_JUMP_HEIGHT_MAP;

  const heights = jumpHeightMap.find(
    (r) => height_at_withers >= r[0] && height_at_withers < r[1]
  );

  return heights
    ? {
        jump_height: heights[2],
        broad_height: heights[3],
      }
    : null;
}

/**
 * Calculate UKC Rally Obedience jump height based on dog's height at withers and jump height type.
 * UKC Rally uses a simple 3-tier system matching the scoresheet checkboxes:
 *   Standard: 6" / 10" / 14"   (≤14" / ≤20" / >20")
 *   Minimum:  4" /  8" / 12"   (≤14" / ≤20" / >20")
 */
export function calculateUkcRallyJumpHeight(
  height_at_withers: number,
  jump_height_type: string
): number | null {
  if (
    ![
      Constants.jump_height_type.minimum,
      Constants.jump_height_type.standard,
    ].includes(jump_height_type)
  ) {
    return null;
  }

  const isStandard = jump_height_type === Constants.jump_height_type.standard;

  if (height_at_withers <= 14) return isStandard ? 6 : 4;
  if (height_at_withers <= 20) return isStandard ? 10 : 8;
  return isStandard ? 14 : 12;
}

/**
 * Calculate UKC Agility jump height based on dog's height at withers and jump height type
 * UKC Agility uses a division system (Toy, Div 1, Div 2, Div 3) with Standard/Minimum options
 * 
 * @param height_at_withers - Dog's height at withers in inches
 * @param jump_height_type - Either "Minimum" or "Standard" jump height type
 * @returns Jump height in inches, or null if invalid input
 */
export function calculateUkcAgilityJumpHeight(
  height_at_withers: number,
  jump_height_type: string
): number | null {
  if (
    ![
      Constants.jump_height_type.minimum,
      Constants.jump_height_type.standard,
    ].includes(jump_height_type)
  ) {
    return null;
  }

  const isStandard = jump_height_type === Constants.jump_height_type.standard;

  // Div Toy (10" and under)
  if (height_at_withers <= 10) {
    return 4; // Both Standard and Minimum = 4"
  }
  
  // Div 1 (10.01" - 14")
  if (height_at_withers <= 14) {
    return isStandard ? 8 : 4;
  }
  
  // Div 2 (14.01" - 20")
  if (height_at_withers <= 20) {
    return isStandard ? 14 : 8;
  }
  
  // Div 3 (over 20")
  return isStandard ? 20 : 14;
}

